import React, { useState } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { Params } from 'react-router-dom'
import {
  giftMsgCardListQueryOption,
  purchaseHistoryDetailQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  GiftMsgCardListSpace,
  GiftSendPurchasedSpace,
  PurchaseHistoryDetailSpace,
  ResponseDataType,
} from 'src/types/api'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import useGiftSendForm from 'src/hooks/react-hook-form/useGiftSendForm.hook'
import { FormObjectType, GiftSendFormItemsType } from 'src/types/react-hook-form'
import { FormProvider } from 'react-hook-form'
import GiftOnlySendView from 'src/pages/GiftSendPage/GiftOnlySendPage/view'
import { DateUtils } from 'src/utils/date'
import {
  giftSendPurchasedQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useModal } from 'src/provider/ModalContextProvider'
import { useGlobalStore } from 'src/store/zustand'

export interface GiftOnlySendPageProps {
  purchaseHistoryDetailData?: PurchaseHistoryDetailSpace.Response
  msgCardInfo: GiftMsgCardListSpace.GiftMsgCardInfoType[] | undefined
  registerAttribute: Partial<Record<keyof GiftSendFormItemsType, Partial<FormObjectType>>>
  handleSelectMsgCard: (cardIdx: number, defaultMsg: string) => void
  purchaseHistoryDetailDataList: ({ text: string; value: string } | null)[]
  handleGiftSendSubmit: () => void
}

export const giftOnlySendPageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { idx } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(giftMsgCardListQueryOption({ Type: 0 })),
      await queryClient.ensureQueryData(purchaseHistoryDetailQueryOption({ Idx: Number(idx) })),
    ])
  }

const GiftOnlySendPage = () => {
  const { idx, couponStatus, couponNumber } = useParamsType()
  const [defaultMsg, setDefaultMsg] = useState<string>('')
  const { DateFormat } = DateUtils
  const { methods, registerAttribute } = useGiftSendForm()
  const { getValues, setValue } = methods
  const { giftSendPurchasedCompletePageNav } = usePageNav()
  const { openModal } = useModal()
  const { setAutoOpenKakaoShare } = useGlobalStore()

  // 메시지 카드 목록 데이터
  const { data: msgCardListQueryData } = useCustomQuery(giftMsgCardListQueryOption({ Type: 0 }))

  const { data: purchaseHistoryDetailData, isLoading: isPurchaseHistoryDetailLoading } =
    useCustomQuery<PurchaseHistoryDetailSpace.Response>(
      purchaseHistoryDetailQueryOption({ Idx: Number(idx) }),
    )

  //선물 발송하기
  const { mutateAsync: giftSendMutate, isPending: isGiftSendPending } = useCustomMutation<
    GiftSendPurchasedSpace.Request,
    GiftSendPurchasedSpace.Response
  >(giftSendPurchasedQueryOption())

  const {
    CouponType,
    CouponOrderID,
    RegDT,
    SalePrice,
    ExpireDT,
    RegularPrice = 0,
  } = purchaseHistoryDetailData?.Body as PurchaseHistoryDetailSpace.Response

  const couponStatusList = {
    '0': '사용 가능',
    '1': '사용 완료',
    '2': '취소',
    '3': '환불',
    '4': '기간 만료',
  }

  const purchaseHistoryDetailDataList = [
    { text: '쿠폰상태', value: couponStatusList[couponStatus] },
    { text: '유효기간', value: DateFormat(ExpireDT, 'YY.MM.DD') },
    { text: '주문번호', value: CouponOrderID ? CouponOrderID : '' },
    { text: '구매 날짜', value: DateFormat(RegDT, 'YY.MM.DD') },
    ...(RegularPrice ? [{ text: '상품 금액', value: `${RegularPrice.toLocaleString()}원` }] : []),
    { text: '결제 금액', value: SalePrice ? `${SalePrice?.toLocaleString()}원` : `` },
  ]

  const handleSelectMsgCard = (cardIdx: number, defaultMsg: string) => {
    setValue('cardIdx', cardIdx)
    if (!getValues('msg')) setDefaultMsg(defaultMsg)
  }

  const handleGiftSendSubmit = () => {
    const isKakaoShare = getValues('sendType') === 0

    giftSendMutate({
      SendType: getValues('sendType'),
      PurchaseIdx: Number(idx),
      CardIdx: getValues('cardIdx'),
      Msg: getValues('msg') || defaultMsg,
      RecipientPNumber: isKakaoShare ? '' : getValues('recipientPNumber').replaceAll('-', ''),
    }).then(({ Header, Body }: ResponseDataType<GiftSendPurchasedSpace.Response>) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        if (isKakaoShare) setAutoOpenKakaoShare(true)
        giftSendPurchasedCompletePageNav(`${CouponType}`, idx, couponNumber, `${Body?.GiftIdx}`)
      } else {
        openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
      }
    })
  }

  const props: GiftOnlySendPageProps = {
    purchaseHistoryDetailData: purchaseHistoryDetailData?.Body,
    msgCardInfo: msgCardListQueryData?.Body?.CardInfo,
    registerAttribute,
    handleSelectMsgCard,
    purchaseHistoryDetailDataList,
    handleGiftSendSubmit,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={isPurchaseHistoryDetailLoading || isGiftSendPending}>
        <Loading.Default />
      </RenderIf>
      <GiftOnlySendView {...props} />
    </FormProvider>
  )
}
export default GiftOnlySendPage
